import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Workforce, WorkforceFormData, StatusChangeReason } from "@/types/workforce";
import { useNavigate } from "react-router-dom";

export const useWorkforceActions = (id?: string, onSuccess?: () => void) => {
	const navigate = useNavigate();

	const handleDelete = async () => {
		if (!id) return;

		try {
			const { error } = await supabase
				.from('workforce')
				.delete()
				.eq('id', id);

			if (error) throw error;

			toast({
				title: "Assignment deleted",
				description: "The workforce assignment has been deleted successfully",
			});

			navigate('/workforce');
		} catch (error) {
			console.error("Error deleting workforce:", error);
			toast({
				title: "Error deleting assignment",
				description: "Could not delete the workforce assignment",
				variant: "destructive",
			});
		}
	};

	const handleUpdate = async (data: Partial<Workforce>, customFieldsValues?: Record<string, any>): Promise<boolean> => {
		if (!id) return false;

		try {
			// Prepare update data
			const updateData: any = { ...data };

			// Handle custom fields
			if (customFieldsValues) {
				updateData.custom_fields = customFieldsValues;
			}

			// Remove nested objects that shouldn't be updated directly
			delete updateData.candidate;
			delete updateData.job;
			delete updateData.assigned_recruiter_profile;

			const { error } = await supabase
				.from('workforce')
				.update(updateData)
				.eq('id', id);

			if (error) throw error;

			if (onSuccess) onSuccess();
			return true;
		} catch (error) {
			console.error("Error updating workforce:", error);
			return false;
		}
	};

	const handleUpdateStatus = async (status: string, extra?: {
		end_date?: string;
		status_change_reason?: StatusChangeReason;
		status_change_note?: string;
	}) => {
		if (!id) return;

		try {
			const updateData: any = { status };

			// Use provided end_date, or fallback to today for terminal statuses
			if (extra?.end_date)
				updateData.end_date = extra.end_date;
			else if (status === 'completed' || status === 'terminated')
				updateData.end_date = new Date().toISOString().split('T')[0];

			if (extra?.status_change_reason)
				updateData.status_change_reason = extra.status_change_reason;

			if (extra?.status_change_note)
				updateData.status_change_note = extra.status_change_note;

			const { error } = await supabase
				.from('workforce')
				.update(updateData)
				.eq('id', id);

			if (error) throw error;

			toast({
				title: "Status updated",
				description: `Assignment status changed to ${status}`,
			});

			if (onSuccess) onSuccess();
		} catch (error) {
			toast({
				title: "Error updating status",
				description: "Could not update the assignment status",
				variant: "destructive",
			});
		}
	};

	return {
		handleDelete,
		handleUpdate,
		handleUpdateStatus
	};
};

export const createWorkforce = async (
	formData: WorkforceFormData,
	organizationUuid: string,
	customFieldsValues?: Record<string, any>
): Promise<string | null> => {
	try {
		const insertData: any = {
			organization_uuid: organizationUuid,
			candidate_id: formData.candidate_id,
			job_id: formData.job_id || null,
			external_job_title: formData.external_job_title || null,
			external_company_name: formData.external_company_name || null,
			external_company_contact: formData.external_company_contact || null,
			external_job_description: formData.external_job_description || null,
			start_date: formData.start_date,
			end_date: formData.end_date || null,
			expected_end_date: formData.expected_end_date || null,
			status: formData.status,
			// Financial - Cost (what we pay)
			purchase_price_daily: formData.purchase_price_daily ? parseFloat(formData.purchase_price_daily) : null,
			purchase_price_hourly: formData.purchase_price_hourly ? parseFloat(formData.purchase_price_hourly) : null,
			// Financial - Revenue (what we charge)
			selling_price_daily: formData.selling_price_daily ? parseFloat(formData.selling_price_daily) : null,
			selling_price_hourly: formData.selling_price_hourly ? parseFloat(formData.selling_price_hourly) : null,
			// Legacy fields
			daily_rate: formData.daily_rate ? parseFloat(formData.daily_rate) : null,
			hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
			currency: formData.currency,
			billing_type: formData.billing_type || null,
			location: formData.location || null,
			location_type: formData.location_type || null,
			work_percentage: formData.work_percentage ? parseInt(formData.work_percentage) : null,
			contract_type: formData.contract_type || null,
			contract_reference: formData.contract_reference || null,
			assigned_recruiter: formData.assigned_recruiter || null,
			notes: formData.notes || null,
			custom_fields: customFieldsValues || {}
		};

		const { data, error } = await supabase
			.from('workforce')
			.insert(insertData)
			.select('id')
			.single();

		if (error) throw error;

		toast({
			title: "Assignment created",
			description: "The workforce assignment has been created successfully",
		});

		return data?.id || null;
	} catch (error) {
		console.error("Error creating workforce:", error);
		toast({
			title: "Error creating assignment",
			description: "Could not create the workforce assignment",
			variant: "destructive",
		});
		return null;
	}
};
